import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SchedulingHistoryContent, { HISTORY_RESULT_LIMIT } from './SchedulingHistoryContent';

export { HISTORY_RESULT_LIMIT };

interface SchedulingHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Set by the per-row link; omitted when opened from the toolbar. */
  initialPronumber?: number;
}

/**
 * Scheduling history as a right-side drawer over the LC/Scheduler boards, so a
 * coordinator can read the full audit trail without leaving the board. Content
 * mounts only while open — nothing is fetched until the user asks for it.
 */
export default function SchedulingHistoryDrawer({
  isOpen,
  onClose,
  initialPronumber,
}: SchedulingHistoryDrawerProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex justify-end bg-scrim/40"
      role="dialog"
      aria-modal="true"
      aria-label="Scheduling History"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-gray-50 dark:bg-gray-900 shadow-2xl w-full max-w-6xl h-full flex flex-col border-l border-slate-200 dark:border-gray-700">
        <div className="flex items-start justify-between px-5 py-3 border-b border-slate-200 dark:border-gray-700 bg-panel">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-body flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-gray-400" />
              Scheduling History
            </h2>
            {initialPronumber != null && (
              <p className="text-xs text-muted mt-0.5">
                Pro #{initialPronumber} — clear the Pro # filter to search all orders
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            aria-label="Close scheduling history"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Keyed so opening on a different pro resets the filter state. */}
          <SchedulingHistoryContent
            key={initialPronumber ?? 'all'}
            initialPronumber={initialPronumber}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
